/**
 * PauseOverlay — full-screen overlay shown when the player presses Escape.
 *
 * Provides Resume, Settings, and Quit to Lobby buttons.
 * Semi-transparent dark background. Camera-fixed. Palette-compliant. Locale-driven.
 * Does not truly pause the server — pauses local rendering/input only.
 *
 * Two renderings, chosen once at construction:
 *   - C5 flag ON  → paper slips on a dimmed indigo veil (DOM, see c5/C5Panel).
 *   - C5 flag OFF → the original Phaser rectangles, untouched.
 * The public API is identical either way, so UIScene never branches.
 */

import * as Phaser from 'phaser';
import { isC5UiEnabled } from './c5/c5Flag';
import { C5Panel, c5QuietButton, c5SlipButton } from './c5/C5Panel';
import {
  PAUSE_OVERLAY_BG,
  PAUSE_PANEL_BG,
  PAUSE_PANEL_BORDER,
  PAUSE_TITLE,
  PAUSE_BUTTON_BG,
  PAUSE_BUTTON_HOVER,
  PAUSE_BUTTON_TEXT,
  hexToCSS,
} from './palette';
import { getString } from './locale';

const OVERLAY_DEPTH = 2500;
const FADE_MS = 200;
const PANEL_WIDTH = 280;
const PANEL_HEIGHT = 220;
const BUTTON_WIDTH = 180;
const BUTTON_HEIGHT = 32;
const BUTTON_GAP = 12;

export class PauseOverlay {
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container;
  private visible = false;

  /** DOM rendering when the C5 flag is on; null otherwise. */
  private panel: C5Panel | null = null;

  // Callbacks
  private onResume: (() => void) | null = null;
  private onSettings: (() => void) | null = null;
  private onQuitLobby: (() => void) | null = null;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    if (isC5UiEnabled()) {
      // Keep an (empty, invisible) container so the field is never null and
      // destroy() stays uniform; nothing is ever added to it.
      this.container = scene.add.container(0, 0);
      this.container.setVisible(false);
      this.buildC5Panel();
      return;
    }
    this.container = scene.add.container(0, 0);
    this.container.setScrollFactor(0);
    this.container.setDepth(OVERLAY_DEPTH);
    this.container.setVisible(false);
    this.container.setAlpha(0);

    this.createPanel();
  }

  setOnResume(cb: () => void): void { this.onResume = cb; }
  setOnSettings(cb: () => void): void { this.onSettings = cb; }
  setOnQuitLobby(cb: () => void): void { this.onQuitLobby = cb; }

  show(): void {
    if (this.visible) return;
    this.visible = true;
    if (this.panel) {
      this.panel.show();
      return;
    }
    this.container.setVisible(true);
    this.scene.tweens.add({
      targets: this.container,
      alpha: 1,
      duration: FADE_MS,
    });
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    if (this.panel) {
      this.panel.hide();
      return;
    }
    this.scene.tweens.add({
      targets: this.container,
      alpha: 0,
      duration: FADE_MS,
      onComplete: () => {
        this.container.setVisible(false);
      },
    });
  }

  isVisible(): boolean {
    return this.visible;
  }

  /** C5 rendering: two paper slips and one quiet band action on the veil. */
  private buildC5Panel(): void {
    const panel = new C5Panel({
      title: getString('menu.pause.title'),
      variant: 'bare',
      width: 260,
    });
    panel.body.append(
      c5SlipButton(getString('menu.pause.resume'), () => this.onResume?.()),
      c5SlipButton(getString('menu.pause.settings'), () => this.onSettings?.()),
      c5QuietButton(getString('menu.pause.quit_lobby'), () => this.onQuitLobby?.()),
    );
    this.panel = panel;
  }

  private createPanel(): void {
    const { width, height } = this.scene.scale;
    const cx = width / 2;
    const cy = height / 2;

    // Full-screen semi-transparent overlay
    const overlay = this.scene.add.rectangle(cx, cy, width, height, PAUSE_OVERLAY_BG, 0.65);
    this.container.add(overlay);

    // Center panel
    const panel = this.scene.add.rectangle(cx, cy, PANEL_WIDTH, PANEL_HEIGHT, PAUSE_PANEL_BG, 0.96);
    panel.setStrokeStyle(2, PAUSE_PANEL_BORDER);
    this.container.add(panel);

    // Title
    const title = this.scene.add.text(cx, cy - PANEL_HEIGHT / 2 + 24, getString('menu.pause.title'), {
      fontSize: '20px',
      fontStyle: 'bold',
      color: hexToCSS(PAUSE_TITLE),
      align: 'center',
    });
    title.setOrigin(0.5, 0.5);
    this.container.add(title);

    // Buttons
    const startY = cy - 20;
    this.addButton(cx, startY, getString('menu.pause.resume'), () => {
      if (this.onResume) this.onResume();
    });
    this.addButton(cx, startY + BUTTON_HEIGHT + BUTTON_GAP, getString('menu.pause.settings'), () => {
      if (this.onSettings) this.onSettings();
    });
    this.addButton(cx, startY + (BUTTON_HEIGHT + BUTTON_GAP) * 2, getString('menu.pause.quit_lobby'), () => {
      if (this.onQuitLobby) this.onQuitLobby();
    });
  }

  private addButton(cx: number, y: number, text: string, onClick: () => void): void {
    const bg = this.scene.add.rectangle(cx, y, BUTTON_WIDTH, BUTTON_HEIGHT, PAUSE_BUTTON_BG);
    bg.setStrokeStyle(1, PAUSE_PANEL_BORDER);
    this.container.add(bg);

    const label = this.scene.add.text(cx, y, text, {
      fontSize: '12px',
      fontStyle: 'bold',
      color: hexToCSS(PAUSE_BUTTON_TEXT),
      align: 'center',
    });
    label.setOrigin(0.5, 0.5);
    this.container.add(label);

    const hitZone = this.scene.add.zone(cx, y, BUTTON_WIDTH, BUTTON_HEIGHT);
    hitZone.setScrollFactor(0);
    hitZone.setDepth(OVERLAY_DEPTH + 1);
    hitZone.setInteractive({ useHandCursor: true });
    this.container.add(hitZone);

    hitZone.on('pointerover', () => bg.setFillStyle(PAUSE_BUTTON_HOVER));
    hitZone.on('pointerout', () => bg.setFillStyle(PAUSE_BUTTON_BG));
    hitZone.on('pointerdown', onClick);
  }

  destroy(): void {
    this.panel?.destroy();
    this.panel = null;
    this.container.destroy();
  }
}
